import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Menu,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_URL } from '../config';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import ProductCard from '../components/ProductCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const SavedMaterials = () => {
    const [boards, setBoards] = useState([]);
    const [savedItems, setSavedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { isAuthenticated } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [newRoomName, setNewRoomName] = useState('');
    const [newRoomDesc, setNewRoomDesc] = useState('');

    useEffect(() => {
        if (isAuthenticated) {
            fetchBoardsAndSavedItems();
        }
    }, [isAuthenticated]);

    const fetchBoardsAndSavedItems = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');
            const [boardsRes, savedRes] = await Promise.all([
                axios.get(`${API_URL}/api/boards`, {
                    headers: { 'x-auth-token': token, 'Content-Type': 'application/json' }
                }),
                axios.get(`${API_URL}/api/user/saved`, {
                    headers: { 'x-auth-token': token, 'Content-Type': 'application/json' }
                })
            ]);

            // Debug print: assignedLinks and savedItems
            const assignedLinks = (boardsRes.data || []).flatMap(board =>
                (board.items || [])
                    .filter(item => item.type === 'product')
                    .map(item => item.ref)
            );
            console.log('Assigned links:', assignedLinks);
            console.log('Boards data:', boardsRes.data);
            console.log('Saved items:', savedRes.data);

            setBoards(boardsRes.data || []);
            setSavedItems(savedRes.data || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load boards or saved items');
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleMoveProduct = async (product, fromBoardId, toBoardId) => {
        try {
            setLoading(true);
            console.log('handleMoveProduct:', { product, productLink: product.link, fromBoardId, toBoardId });

            // Validate inputs
            if (!product || !product.link) {
                throw new Error('Invalid product data');
            }

            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');

            // Remove from old board (if any)
            if (fromBoardId) {
                const fromBoard = boards.find(b => b._id === fromBoardId);
                if (!fromBoard) {
                    throw new Error('Source board not found');
                }
                const updatedFromItems = (fromBoard.items || []).filter(item => item.ref !== product.link);
                await axios.put(
                    `${API_URL}/api/boards/${fromBoardId}`,
                    { ...fromBoard, items: updatedFromItems },
                    { headers: { 'x-auth-token': token } }
                );
            }

            // Add to new board
            const toBoard = boards.find(b => b._id === toBoardId);
            if (!toBoard) {
                throw new Error('Destination board not found');
            }

            const updatedToItems = [...(toBoard.items || []), {
                type: 'product',
                ref: product.link
            }];

            await axios.put(
                `${API_URL}/api/boards/${toBoardId}`,
                { ...toBoard, items: updatedToItems },
                { headers: { 'x-auth-token': token } }
            );

            // Refresh the data
            await fetchBoardsAndSavedItems();
        } catch (err) {
            console.error('Move product error:', err);
            setError(err.response?.data?.message || err.message || 'Failed to move product');
        } finally {
            setLoading(false);
            setAnchorEl(null);
            setSelectedProduct(null);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedProduct(null);
    };

    const handleCreateRoom = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');
            await axios.post(
                `${API_URL}/api/boards`,
                { name: newRoomName, description: newRoomDesc },
                { headers: { 'x-auth-token': token } }
            );
            setOpenDialog(false);
            setNewRoomName('');
            setNewRoomDesc('');
            fetchBoardsAndSavedItems();
        } catch (err) {
            setError('Failed to create room');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveProduct = async (product) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');
            await axios.delete(`${API_URL}/api/user/saved`, {
                headers: { 'x-auth-token': token },
                data: { link: product.link }
            });
            await fetchBoardsAndSavedItems();
        } catch (err) {
            setError('Failed to remove product');
        } finally {
            setLoading(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ mt: 4 }}>
                    <Alert severity="info">
                        Please log in to view your saved materials
                    </Alert>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{ flexGrow: 1, fontFamily: "'Inter', 'Roboto', 'Arial', sans-serif", fontWeight: 600 }}
                    >
                        Saved Materials by Room
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setOpenDialog(true)}
                        sx={{ minWidth: 40, borderRadius: '50%', fontSize: 28, p: 0, width: 48, height: 48 }}
                    >
                        +
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : boards.length === 0 ? (
                    <Alert severity="info">
                        You haven't created any rooms/boards yet
                    </Alert>
                ) : (
                    <>
                        {boards.map(board => {
                            // For each board, resolve product refs to actual saved items
                            const products = (board.items || [])
                                .filter(item => item.type === 'product')
                                .map(item => savedItems.find(si => si.link === item.ref))
                                .filter(Boolean);
                            return (
                                <Accordion key={board._id} defaultExpanded sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6" sx={{ fontFamily: "'Inter', 'Roboto', 'Arial', sans-serif", fontWeight: 600 }}>{board.name}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {products.length === 0 ? (
                                            <Alert severity="info">No products saved for this room yet.</Alert>
                                        ) : (
                                            products.map(product => (
                                                <ProductCard
                                                    key={product._id || product.link}
                                                    product={product}
                                                    boardList={boards}
                                                    currentBoardId={board._id}
                                                    onMove={handleMoveProduct}
                                                    onRemove={handleRemoveProduct}
                                                />
                                            ))
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                        {/* Unassigned section as Accordion */}
                        {(() => {
                            const assignedLinks = boards.flatMap(board => (board.items || []).filter(item => item.type === 'product').map(item => item.ref));
                            const unassigned = savedItems.filter(item => !assignedLinks.includes(item.link));
                            return unassigned.length > 0 ? (
                                <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6" sx={{ fontFamily: "'Inter', 'Roboto', 'Arial', sans-serif", fontWeight: 600 }}>Unassigned</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {unassigned.map(product => (
                                            <ProductCard
                                                key={product._id || product.link}
                                                product={product}
                                                boardList={boards}
                                                currentBoardId={null}
                                                onMove={handleMoveProduct}
                                                onRemove={handleRemoveProduct}
                                            />
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            ) : null;
                        })()}
                    </>
                )}

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    {boards.filter(b => b._id !== selectedProduct?.fromBoardId).map(b => (
                        <MenuItem key={b._id} onClick={() => handleMoveProduct(selectedProduct, selectedProduct.fromBoardId, b._id)}>
                            Move to {b.name}
                        </MenuItem>
                    ))}
                </Menu>
                <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                    <DialogTitle>Create New Room</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Room Name"
                            fullWidth
                            value={newRoomName}
                            onChange={e => setNewRoomName(e.target.value)}
                        />
                        <TextField
                            margin="dense"
                            label="Description"
                            fullWidth
                            value={newRoomDesc}
                            onChange={e => setNewRoomDesc(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreateRoom} disabled={!newRoomName.trim() || loading} variant="contained">Create</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default SavedMaterials; 